"use client";

import { useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { Loader2 } from "lucide-react";

interface CommentModalProps {
  isOpen: boolean;
  onClose: () => void;
  opportunityId: string;
  currentComment: string | null;
  onSuccess: () => void;
}

export const CommentModal = ({
  isOpen,
  onClose,
  opportunityId,
  currentComment,
  onSuccess,
}: CommentModalProps) => {
  const { toast } = useToast();
  const [comment, setComment] = useState(currentComment || "");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      await axios.put(`/api/crm/opportunity/${opportunityId}/comments`, {
        comments: comment,
      });

      toast({
        title: "Success",
        description: "Comment updated successfully",
      });
      
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error updating comment:", error);
      toast({
        title: "Error",
        description: "Failed to update comment",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Opportunity Comment</DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <Textarea
            placeholder="Add your comments here..."
            className="min-h-[150px]"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            disabled={isLoading}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};